import { StatusInfo } from "@george-labs.com/design-system"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useSelectedSapId } from "../../app/hooks"
import { getCases } from "../../app/slices/casesSlice"
import { getCaseEvents } from "../../app/slices/caseEventsSlice"
import { clearOffers, getOffers, setOffer } from "../../app/slices/offersSlice"
import { useOfferScreenFlow } from "../../hooks/useOfferScreenFlow"
import { StatusInfoComponent } from "../../Shared"
import Cases from "./Cases"
import Offers from "../Offer/Offers"
import { getAndUnsetDeeplink, KLIKK_DEEPLINK } from "../../utils/deeplink"

const CaseAndOfferLoader = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const sapId = useSelectedSapId()
  const { goToNextOfferScreen } = useOfferScreenFlow()
  const [hasError, setHasError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const isInitialized = useRef(false)

  useEffect(() => {
    if (!sapId || isInitialized.current) {
      return
    }

    isInitialized.current = true

    const loadData = async () => {
      try {
        setIsLoading(true)

        const [offersResult] = await Promise.all([
          dispatch(getOffers()),
          dispatch(getCases()),
          dispatch(getCaseEvents()),
        ])

        const deeplink = getAndUnsetDeeplink()
        if (deeplink === KLIKK_DEEPLINK && getOffers.fulfilled.match(offersResult)) {
          const offers = offersResult.payload?.palOffers
          if (offers?.length === 1) {
            dispatch(setOffer(offers[0]))
            const navigated = await goToNextOfferScreen(
              null,
              offers[0].sourceId,
              true,
            )
            if (navigated) {
              return
            }
          }
        }

        setIsLoading(false)
      } catch {
        setHasError(true)
      }
    }

    loadData()

    return () => {
      dispatch(clearOffers())
    }
  }, [sapId, dispatch])

  if (hasError) {
    return (
      <StatusInfoComponent
        variant={StatusInfo.VARIANT.ERROR}
      ></StatusInfoComponent>
    )
  }

  if (isLoading) {
    return (
      <StatusInfo
        icon={StatusInfo.ICON.EMPTY_STATE_GENERIC}
        title={t("cases.loading")}
      />
    )
  }

  return (
    <>
      {isInitialized && (
        <>
          <Offers />
          <Cases />
        </>
      )}
    </>
  )
}

export default CaseAndOfferLoader
